import { useState } from 'react';
import { useEditMode } from '@/context/EditModeContext';
import { useSiteData } from '@/context/SiteDataContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Plus, ChevronUp, ChevronDown, Trash2, Loader2 } from 'lucide-react';

/**
 * FaithItems
 * ----------
 * Renders an editable array of Statement-of-Faith bullets.
 * Storage: page_content.about.faith_items = [{id, order, title, text}].
 * Edit mode admins can add, edit, reorder, and remove items inline.
 */
export default function FaithItems({ items: itemsProp }) {
  const { editMode } = useEditMode();
  const { loadPageContent } = useSiteData();
  const [busy, setBusy] = useState(false);
  const items = (itemsProp || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  const persist = async (next) => {
    setBusy(true);
    try {
      await api.patch('/site/page-content/about', { faith_items: next });
      await loadPageContent('about');
      toast.success('Saved');
    } catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const addOne = () => {
    const nextOrder = items.length ? Math.max(...items.map(i => i.order ?? 0)) + 1 : 0;
    const next = [...items, { id: `sof_${Date.now()}`, order: nextOrder, title: 'New belief', text: 'Describe this belief here.' }];
    persist(next);
  };
  const updateOne = (id, patch) => persist(items.map(i => i.id === id ? { ...i, ...patch } : i));
  const removeOne = (id) => {
    if (!window.confirm('Remove this belief statement?')) return;
    persist(items.filter(i => i.id !== id));
  };
  const move = (id, delta) => {
    const idx = items.findIndex(i => i.id === id);
    const swap = idx + delta;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    persist(next.map((it, i) => ({ ...it, order: i })));
  };

  return (
    <div className="space-y-6 text-charcoal-muted leading-relaxed text-lg">
      {items.map((it, i) => (
        <div key={it.id} data-testid={`faith-item-${it.id}`} className="relative group">
          {editMode ? (
            <div className="border border-dashed border-gold/40 rounded-lg p-4 bg-white/50">
              <input
                data-testid={`faith-title-${it.id}`}
                value={it.title || ''}
                onChange={(e) => updateOne(it.id, { title: e.target.value })}
                className="w-full font-bold text-royal text-lg border-b border-gold/30 pb-1 mb-2 bg-transparent focus:outline-none"
                placeholder="Belief title (e.g. 'The Scriptures')"
              />
              <textarea
                data-testid={`faith-text-${it.id}`}
                value={it.text || ''}
                onChange={(e) => updateOne(it.id, { text: e.target.value })}
                rows={2}
                className="w-full text-charcoal-muted bg-transparent focus:outline-none resize-y"
                placeholder="Belief text"
              />
              <div className="flex items-center gap-1 mt-2 justify-end">
                <button onClick={() => move(it.id, -1)} disabled={i === 0 || busy} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => move(it.id, 1)} disabled={i === items.length - 1 || busy} className="p-1.5 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                <button onClick={() => removeOne(it.id)} disabled={busy} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ) : (
            <p><strong className="text-royal">{it.title}</strong> — {it.text}</p>
          )}
        </div>
      ))}
      {editMode && (
        <button
          onClick={addOne}
          disabled={busy}
          data-testid="faith-add-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-royal text-white text-sm font-semibold hover:bg-gold hover:text-royal-dark transition disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add belief statement
        </button>
      )}
    </div>
  );
}
